import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wrench, Plus, Edit2, Trash2, X, Save, Calendar, User, Building2, Upload, GripVertical, Images, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useSupabaseReferences } from '../hooks/useSupabaseReferences';
import { ProjectReference, updateReference } from '../lib/supabase';
import { compressProductImage } from '../lib/imageCompression';

export default function NeserioveVyrobkyPage() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language as 'en' | 'de' | 'cz' | 'sk';
  const location = useLocation();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showReferenceModal, setShowReferenceModal] = useState(false);
  const [editingReference, setEditingReference] = useState<ProjectReference | null>(null);
  const [draggedRefIndex, setDraggedRefIndex] = useState<number | null>(null);
  const [detailReference, setDetailReference] = useState<ProjectReference | null>(null);

  const openDetail = (reference: ProjectReference) => {
    setDetailReference(reference);
    document.body.style.overflow = 'hidden';
    window.dispatchEvent(new Event('modalOpen'));
  };

  const closeDetail = () => {
    setDetailReference(null);
    document.body.style.overflow = 'unset';
    window.dispatchEvent(new Event('modalClose'));
  };

  const isAdmin = new URLSearchParams(location.search).get('admin') === 'true' ||
                  localStorage.getItem('admin_logged_in') === 'true';

  const { references, loading, addReference, updateReference: updateRef, deleteReference } = useSupabaseReferences('neseriove');

  const categories = Array.from(new Set(references.map(r => r.category))).filter(Boolean);

  const filteredReferences = selectedCategory
    ? references.filter(r => r.category === selectedCategory)
    : references;

  const sortedReferences = [...filteredReferences].sort((a, b) => a.order_index - b.order_index);

  const handleAddReference = () => {
    setEditingReference(null);
    setShowReferenceModal(true);
  };

  const handleEditReference = (reference: ProjectReference) => {
    setEditingReference(reference);
    setShowReferenceModal(true);
  };

  const handleDeleteReference = async (id: string) => {
    if (window.confirm('Opravdu chcete smazat tuto položku?')) {
      try {
        await deleteReference(id);
      } catch (error) {
        console.error('Error deleting item:', error);
        alert('Chyba při mazání položky');
      }
    }
  };

  const handleRefDragStart = (e: React.DragEvent, index: number) => {
    setDraggedRefIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleRefDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleRefDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedRefIndex === null || draggedRefIndex === dropIndex) return;

    const reordered = [...sortedReferences];
    const dragged = reordered[draggedRefIndex];
    reordered.splice(draggedRefIndex, 1);
    reordered.splice(dropIndex, 0, dragged);

    try {
      await Promise.all(reordered.map((ref, index) =>
        updateReference(ref.id, { order_index: index })
      ));
      await updateRef(reordered[0].id, { order_index: 0 });
    } catch (err) {
      console.error('Error updating order:', err);
    }
    setDraggedRefIndex(null);
  };

  const getTitle = (reference: ProjectReference) => {
    const langKey = `title_${currentLang}` as keyof ProjectReference;
    return (reference[langKey] as string) || reference.title_cz;
  };

  const getDescription = (reference: ProjectReference) => {
    const langKey = `description_${currentLang}` as keyof ProjectReference;
    return (reference[langKey] as string) || reference.description_cz;
  };

  const getDisplayImage = (reference: ProjectReference) => {
    if (reference.image_url) return reference.image_url;
    if (reference.images && reference.images.length > 0) return reference.images[0];
    return null;
  };

  const getAllImages = (reference: ProjectReference): string[] => {
    const imgs: string[] = [];
    if (reference.image_url) imgs.push(reference.image_url);
    if (reference.images) imgs.push(...reference.images);
    return imgs;
  };

  const pageTitle = i18n.language === 'en' ? 'Custom Products' :
                    i18n.language === 'de' ? 'Sonderanfertigungen' :
                    i18n.language === 'sk' ? 'Nesériové výrobky' :
                    'Nesériové výrobky';

  const pageSubtitle = i18n.language === 'en' ? 'Custom development and bespoke manufacturing for special projects' :
                       i18n.language === 'de' ? 'Kundenentwicklung und Sonderfertigung für Spezialprojekte' :
                       i18n.language === 'sk' ? 'Zákaznícky vývoj a zákazková výroba pre špeciálne projekty' :
                       'Zákaznický vývoj a zakázková výroba pro speciální projekty';

  return (
    <div className="min-h-screen pt-20 pb-16">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Wrench className="w-10 h-10 text-blue-400" />
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
              {pageTitle}
            </h1>
          </div>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            {pageSubtitle}
          </p>

          {isAdmin && (
            <div className="flex justify-center gap-4 mt-6">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleAddReference}
                className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all"
              >
                <Plus className="w-5 h-5" />
                Přidat položku
              </motion.button>
            </div>
          )}
        </motion.div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="text-slate-400 mt-4">{t('common.loading', 'Načítám...')}</p>
          </div>
        ) : (
          <>
            {categories.length > 0 && (
              <div className="mb-8 flex flex-wrap gap-3 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedCategory(null)}
                  className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
                    selectedCategory === null
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/25'
                      : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50 border border-slate-700'
                  }`}
                >
                  {t('common.all', 'Vše')}
                </motion.button>
                {categories.map((category) => (
                  <motion.button
                    key={category}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
                      selectedCategory === category
                        ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/25'
                        : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50 border border-slate-700'
                    }`}
                  >
                    {category}
                  </motion.button>
                ))}
              </div>
            )}

            {sortedReferences.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-16"
              >
                <Wrench className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-lg">
                  Zatím nejsou k dispozici žádné nesériové výrobky.
                </p>
              </motion.div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sortedReferences.map((reference, index) => (
                  <motion.div
                    key={reference.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    draggable={isAdmin}
                    onDragStart={isAdmin ? (e) => handleRefDragStart(e, index) : undefined}
                    onDragOver={isAdmin ? handleRefDragOver : undefined}
                    onDrop={isAdmin ? (e) => handleRefDrop(e, index) : undefined}
                    onClick={() => !isAdmin && openDetail(reference)}
                    className={`group relative bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-xl overflow-hidden border border-slate-700/50 hover:border-blue-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/10 ${isAdmin ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} ${draggedRefIndex === index ? 'opacity-50 scale-95' : ''}`}
                  >
                    {isAdmin && (
                      <div className="absolute top-2 left-2 z-10 bg-black/60 rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <GripVertical className="w-4 h-4 text-slate-400" />
                      </div>
                    )}
                    <div className="relative aspect-video bg-slate-900 overflow-hidden">
                      {getDisplayImage(reference) ? (
                        <>
                          <img
                            src={getDisplayImage(reference)!}
                            alt={getTitle(reference)}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />
                          {reference.images && reference.images.length > 1 && (
                            <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                              <Images className="w-3 h-3" />
                              {reference.images.length}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Wrench className="w-12 h-12 text-slate-700" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    </div>

                    <div className="p-5">
                      <h3 className="text-lg font-semibold text-white mb-2 line-clamp-2 group-hover:text-blue-400 transition-colors">
                        {getTitle(reference)}
                      </h3>
                      <p className="text-slate-400 text-sm line-clamp-3 mb-4">
                        {getDescription(reference)}
                      </p>

                      <div className="space-y-2 mb-4">
                        {reference.client_name && (
                          <div className="flex items-center gap-2 text-slate-400 text-sm">
                            <User className="w-4 h-4 text-blue-400" />
                            <span>{reference.client_name}</span>
                          </div>
                        )}
                        {reference.project_date && (
                          <div className="flex items-center gap-2 text-slate-400 text-sm">
                            <Calendar className="w-4 h-4 text-blue-400" />
                            <span>{new Date(reference.project_date).toLocaleDateString(currentLang)}</span>
                          </div>
                        )}
                        {reference.category && (
                          <div className="inline-block px-3 py-1 bg-blue-500/10 text-blue-400 rounded-full text-xs font-medium border border-blue-500/20">
                            {reference.category}
                          </div>
                        )}
                      </div>

                      {isAdmin ? (
                        <div className="flex gap-2 mt-4">
                          <button
                            onClick={(e) => { e.stopPropagation(); openDetail(reference); }}
                            className="flex-1 p-2 bg-blue-700/60 hover:bg-blue-600 rounded text-white transition-colors flex items-center justify-center gap-2 text-sm"
                          >
                            <Wrench className="w-4 h-4" />
                            Detail
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEditReference(reference); }}
                            className="flex-1 p-2 bg-blue-600/80 hover:bg-blue-500 rounded text-white transition-colors flex items-center justify-center gap-2 text-sm"
                          >
                            <Edit2 className="w-4 h-4" />
                            {t('common.edit', 'Upravit')}
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteReference(reference.id); }}
                            className="p-2 bg-red-600/80 hover:bg-red-500 rounded text-white transition-colors flex items-center justify-center"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="text-blue-400 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                          Zobrazit detail
                          <ChevronRight className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <AnimatePresence>
        {detailReference && (
          <DetailModal
            reference={detailReference}
            getTitle={getTitle}
            getDescription={getDescription}
            getAllImages={getAllImages}
            onClose={closeDetail}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showReferenceModal && (
          <ItemModal
            reference={editingReference}
            onClose={() => setShowReferenceModal(false)}
            onSave={async (referenceData) => {
              try {
                if (editingReference) {
                  await updateRef(editingReference.id, referenceData);
                } else {
                  await addReference({ ...referenceData, category: referenceData.category || 'neseriove' } as Omit<ProjectReference, 'id' | 'created_at' | 'updated_at'>);
                }
                setShowReferenceModal(false);
              } catch (error) {
                console.error('Error saving item:', error);
                alert('Chyba při ukládání: ' + (error as Error).message);
              }
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function DetailModal({
  reference,
  getTitle,
  getDescription,
  getAllImages,
  onClose,
}: {
  reference: ProjectReference;
  getTitle: (r: ProjectReference) => string;
  getDescription: (r: ProjectReference) => string;
  getAllImages: (r: ProjectReference) => string[];
  onClose: () => void;
}) {
  const images = getAllImages(reference);
  const [currentImg, setCurrentImg] = useState(0);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        className="bg-slate-900 border border-blue-500/30 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-20 bg-black/60 hover:bg-black/80 text-white rounded-full p-1.5 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        {images.length > 0 && (
          <div className="relative aspect-video bg-slate-950 rounded-t-2xl overflow-hidden">
            <img
              src={images[currentImg]}
              alt={getTitle(reference)}
              className="w-full h-full object-cover"
            />
            {images.length > 1 && (
              <>
                <button
                  onClick={() => setCurrentImg(i => (i - 1 + images.length) % images.length)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setCurrentImg(i => (i + 1) % images.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {images.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentImg(i)}
                      className={`w-2 h-2 rounded-full transition-all ${i === currentImg ? 'bg-blue-400 w-4' : 'bg-white/40 hover:bg-white/70'}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <div className="p-6">
          <div className="flex items-start justify-between mb-4 pr-8">
            <h2 className="text-2xl font-bold text-white">{getTitle(reference)}</h2>
          </div>

          <div className="flex flex-wrap gap-3 mb-5">
            {reference.client_name && (
              <div className="flex items-center gap-1.5 text-slate-400 text-sm">
                <User className="w-4 h-4 text-blue-400" />
                <span>{reference.client_name}</span>
              </div>
            )}
            {reference.project_date && (
              <div className="flex items-center gap-1.5 text-slate-400 text-sm">
                <Calendar className="w-4 h-4 text-blue-400" />
                <span>{new Date(reference.project_date).toLocaleDateString()}</span>
              </div>
            )}
            {reference.category && (
              <span className="px-3 py-1 bg-blue-500/10 text-blue-400 rounded-full text-xs border border-blue-500/20 font-medium">
                {reference.category}
              </span>
            )}
          </div>

          <p className="text-slate-300 leading-relaxed whitespace-pre-wrap mb-5">
            {getDescription(reference)}
          </p>

          {images.length > 1 && (
            <div className="mt-5">
              <p className="text-slate-400 text-sm mb-2">Galerie ({images.length} obrázků)</p>
              <div className="grid grid-cols-5 gap-2">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentImg(i)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${i === currentImg ? 'border-blue-400' : 'border-transparent opacity-60 hover:opacity-100'}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

function ItemModal({
  reference,
  onClose,
  onSave,
}: {
  reference: ProjectReference | null;
  onClose: () => void;
  onSave: (data: Partial<ProjectReference>) => void;
}) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title_cz: reference?.title_cz || '',
    title_en: reference?.title_en || '',
    title_de: reference?.title_de || '',
    title_sk: reference?.title_sk || '',
    description_cz: reference?.description_cz || '',
    description_en: reference?.description_en || '',
    description_de: reference?.description_de || '',
    description_sk: reference?.description_sk || '',
    client_name: reference?.client_name || '',
    project_date: reference?.project_date || new Date().toISOString().split('T')[0],
    image_url: reference?.image_url || '',
    images: reference?.images || [] as string[],
    category: reference?.category || 'neseriove',
    order_index: reference?.order_index ?? 0,
  });
  const [imageUploading, setImageUploading] = useState(false);
  const [draggedImgIdx, setDraggedImgIdx] = useState<number | null>(null);

  const handleMainImageUpload = async (file: File) => {
    try {
      setImageUploading(true);
      const compressed = await compressProductImage(file);
      setFormData(prev => ({ ...prev, image_url: compressed }));
    } catch (error) {
      console.error('Error compressing image:', error);
      alert('Chyba při nahrávání obrázku');
    } finally {
      setImageUploading(false);
    }
  };

  const handleGalleryImagesUpload = async (files: FileList) => {
    setImageUploading(true);
    const newImages: string[] = [];
    for (let i = 0; i < files.length; i++) {
      try {
        const compressed = await compressProductImage(files[i]);
        newImages.push(compressed);
      } catch (error) {
        console.error('Error compressing image:', error);
      }
    }
    setFormData(prev => ({ ...prev, images: [...prev.images, ...newImages] }));
    setImageUploading(false);
  };

  const removeGalleryImage = (index: number) => {
    setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const handleImgDragStart = (index: number) => {
    setDraggedImgIdx(index);
  };

  const handleImgDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedImgIdx === null || draggedImgIdx === index) return;
    const newImages = [...formData.images];
    const dragged = newImages[draggedImgIdx];
    newImages.splice(draggedImgIdx, 1);
    newImages.splice(index, 0, dragged);
    setFormData(prev => ({ ...prev, images: newImages }));
    setDraggedImgIdx(index);
  };

  const handleImgDragEnd = () => {
    setDraggedImgIdx(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-slate-900 border border-blue-500/30 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">
            {reference ? 'Upravit položku' : 'Přidat položku'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-slate-300 mb-2">
              Název <span className="text-blue-400">*</span>
            </label>
            <input
              type="text"
              value={formData.title_cz}
              onChange={(e) => setFormData({ ...formData, title_cz: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-slate-300 mb-2">
              Popis <span className="text-blue-400">*</span>
            </label>
            <textarea
              value={formData.description_cz}
              onChange={(e) => setFormData({ ...formData, description_cz: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
              rows={3}
              required
            />
          </div>

          <div>
            <label className="block text-slate-300 mb-2">Hlavní obrázek</label>
            <div className="space-y-2">
              <label className={`flex items-center justify-center gap-2 w-full border-2 border-dashed border-slate-600 hover:border-blue-500/50 rounded-lg p-4 cursor-pointer transition-colors ${imageUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleMainImageUpload(file);
                  }}
                />
                <Upload className="w-5 h-5 text-slate-400" />
                <span className="text-slate-400 text-sm">
                  {imageUploading ? 'Nahrávám...' : 'Klikněte pro nahrání hlavního obrázku'}
                </span>
              </label>
              {formData.image_url && (
                <div className="relative aspect-video rounded-lg overflow-hidden bg-slate-800">
                  <img src={formData.image_url} alt="Preview" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                    className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 rounded-full p-1 text-white transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-slate-300 mb-2 flex items-center gap-2">
              <Images className="w-4 h-4 text-blue-400" />
              Galerie obrázků
            </label>
            <label className={`flex items-center justify-center gap-2 w-full border-2 border-dashed border-slate-600 hover:border-blue-500/50 rounded-lg p-4 cursor-pointer transition-colors mb-3 ${imageUploading ? 'opacity-50 pointer-events-none' : ''}`}>
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    handleGalleryImagesUpload(e.target.files);
                  }
                }}
              />
              <Upload className="w-5 h-5 text-slate-400" />
              <span className="text-slate-400 text-sm">
                {imageUploading ? 'Nahrávám...' : 'Nahrát více obrázků do galerie'}
              </span>
            </label>
            {formData.images.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {formData.images.map((img, idx) => (
                  <div
                    key={idx}
                    draggable
                    onDragStart={() => handleImgDragStart(idx)}
                    onDragOver={(e) => handleImgDragOver(e, idx)}
                    onDragEnd={handleImgDragEnd}
                    className={`relative aspect-square rounded-lg overflow-hidden bg-slate-800 cursor-grab active:cursor-grabbing group ${draggedImgIdx === idx ? 'opacity-50 scale-95' : ''}`}
                  >
                    <img src={img} alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <GripVertical className="w-5 h-5 text-white" />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeGalleryImage(idx)}
                      className="absolute top-1 right-1 bg-red-600/80 hover:bg-red-500 rounded-full p-0.5 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                    <span className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">
                      {idx + 1}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-300 mb-2">Klient</label>
              <input
                type="text"
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-slate-300 mb-2">Datum</label>
              <input
                type="date"
                value={formData.project_date}
                onChange={(e) => setFormData({ ...formData, project_date: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-slate-300 mb-2">Kategorie</label>
            <input
              type="text"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
              placeholder="např. CNC, Agro, Automotive..."
            />
          </div>

          <details className="group">
            <summary className="cursor-pointer text-slate-400 hover:text-slate-200 text-sm py-2 select-none">
              Překlady (volitelné)
            </summary>
            <div className="mt-3 space-y-4 border-t border-slate-700 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-400 text-sm mb-1">Název (SK)</label>
                  <input type="text" value={formData.title_sk} onChange={(e) => setFormData({ ...formData, title_sk: e.target.value })} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-slate-400 text-sm mb-1">Název (EN)</label>
                  <input type="text" value={formData.title_en} onChange={(e) => setFormData({ ...formData, title_en: e.target.value })} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-slate-400 text-sm mb-1">Název (DE)</label>
                  <input type="text" value={formData.title_de} onChange={(e) => setFormData({ ...formData, title_de: e.target.value })} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-blue-500" />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="block text-slate-400 text-sm mb-1">Popis (SK)</label>
                  <textarea value={formData.description_sk} onChange={(e) => setFormData({ ...formData, description_sk: e.target.value })} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-blue-500" rows={2} />
                </div>
                <div>
                  <label className="block text-slate-400 text-sm mb-1">Popis (EN)</label>
                  <textarea value={formData.description_en} onChange={(e) => setFormData({ ...formData, description_en: e.target.value })} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-blue-500" rows={2} />
                </div>
                <div>
                  <label className="block text-slate-400 text-sm mb-1">Popis (DE)</label>
                  <textarea value={formData.description_de} onChange={(e) => setFormData({ ...formData, description_de: e.target.value })} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-blue-500" rows={2} />
                </div>
              </div>
            </div>
          </details>

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              {t('common.cancel', 'Zrušit')}
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {t('common.save', 'Uložit')}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
